import {getDateToString} from '../../utils/dateUtils';

/**
 * Returns a date object when given a string, "YYYY-MM-DD".
 *
 * @param {string} dateString - date string in the format "YYYY-MM-DD"
 * @return {Date}  New Date object from provided string.
 */
function getDateFromString(dateString) {
    const dateElems = dateString.split('-');
    return new Date(+dateElems[0], +dateElems[1] - 1, +dateElems[2]);
}

/**
 * Returns string representation of a date when given a custom
 * object with year, month, day. That custom object is provided
 * by the calendar-base data.
 * The result is in the format "YYYY-MM-DD".
 *
 * @param {object} obj - custom date object
 * @return {string}  string representation of date.
 */
function getDayToString(obj) {
    let {year, month, day} = obj;
    month = month + 1;
    month = month < 10 ? `0${month}` : month;
    day = day < 10 ? `0${day}` : day;
    return `${year}-${month}-${day}`;
}

/**
 * Returns a javascript Date object given a custom Day object provided
 * by the calendar-base data.
 * @param {object} obj - custom date object
 * @return {Date} javascript Date object
 */
function getDayToDate(obj) {
    let {year, month, day} = obj;
    return new Date(year, month, day);
}

/**
 * Returns an array of date objects given a start date and a number
 * of nights.
 *
 * @param {string} start - start date in format, "YYYY-MM-DD"
 * @param {number} num - number of nights
 * @return {array}  Array of date objects
 */
function getDateRange(start, num) {
    let startDate = getDateFromString(start);
    let dates = [getDateToString(new Date(startDate))];

    for (let i = 0; i < num; i++) {
        dates.push(
            getDateToString(
                new Date(startDate.setDate(startDate.getDate() + 1))
            )
        );
    }
    return dates;
}

/**
 * Returns the number of days between two date strings.
 *
 * @param {string} startString - start date string in format, "YYYY-MM-DD"
 * @param {string} endString - end date string in format, "YYYY-MM-DD"
 * @return {number}  number of days
 */
function getNumDaysBetweenDateStrings(startString, endString) {
    let numDays = 0;
    const startDate = getDateFromString(startString);
    const endDate = getDateFromString(endString);
    while (endDate > startDate) {
        numDays++;
        startDate.setDate(startDate.getDate() + 1);
    }
    return numDays;
}

/**
 * Returns whether the date represented by the given custom date object is the last day of the given month.
 * @param day {object} The custom day object to check
 * @returns {boolean} true if it is the last day of the given month, otherwise false
 */
function isLastDayInMonth(day) {
    let test = getDayToDate(day);
    if (test.getDate() < 28) {
        return false;
    }
    test.setDate(test.getDate() + 1);
    return test.getDate() === 1;
}

/**
 * Returns whether the date represented by the given custom date object is the first day of the given month.
 * @param day {object} The custom day object to check
 * @returns {boolean} true if it is the first day of the given month, otherwise false
 */
function isFirstDayInMonth(day) {
    let test = getDayToDate(day);
    if (test.getDate() !== 1) {
        return false;
    }

    return true;
}

/**
 * Given a month, add a number of months to it and return the new month.
 * @param year {object} Year of the month
 * @param month {object} month number (zero-based)
 * @param num {object} Number of months to add
 * @returns {object} object with the year and month of the new month
 */
function addMonthsToMonth(year, month, num) {
    const date = getDayToDate({year, month, day: '01'});
    date.setMonth(date.getMonth() + num);
    return {year: date.getFullYear(), month: date.getMonth()};
}

export default {
    getDateFromString,
    getDayToString,
    getDayToDate,
    getDateRange,
    getNumDaysBetweenDateStrings,
    isFirstDayInMonth,
    isLastDayInMonth,
    addMonthsToMonth,
};
